import { useEffect, useMemo } from 'react';
import { safeHex } from '@/lib/colors';
import type { LiveBus } from '@/lib/live-buses';
import { badgeIcon, busIcon, INACTIVE, registerIcons, WARN_ICON } from './icons';
import { fc, GeoJsonLayer } from './layers';
import { useMap } from './map-view';

const iconSize = ['interpolate', ['linear'], ['zoom'], 10, 0.45, 13, 0.7, 16, 1, 18, 1.25];

/**
 * Live buses as rotated bus icons in their line colour with a line-number badge. Buses not in
 * service are grey. Hidden buses (not shown in the app) are left out unless `showHidden`.
 */
export function LiveBusesLayer({
  buses,
  highlight,
  showHidden = false,
  fadeOthers = false,
  warnings,
  onSelect,
}: {
  buses: LiveBus[];
  highlight?: string | null;
  /** Fades every bus except the highlighted one. */
  fadeOthers?: boolean;
  /** Buses that get a small warning badge (e.g. driving another zone than assigned). */
  warnings?: Set<string>;
  showHidden?: boolean;
  onSelect?: (id: string) => void;
}) {
  const map = useMap();
  useEffect(() => {
    if (map) registerIcons(map);
  }, [map]);

  const data = useMemo(
    () =>
      fc(
        buses
          .filter((b) => b.geometry && (showHidden || b.is_visible))
          .map((b) => {
            const color = b.is_active ? safeHex(b.color, '#0b3f82') : INACTIVE;
            const label = (b.number ?? '').trim();
            return {
              type: 'Feature' as const,
              geometry: b.geometry!,
              properties: {
                id: b._id,
                heading: Number(b.heading) || 0,
                bus: busIcon(color),
                badge: label ? badgeIcon(color, label) : '',
                active: b.is_active,
                fade: fadeOthers && highlight && b._id !== highlight ? 0.25 : 1,
                top: b._id === highlight ? 2 : b.is_active ? 1 : 0,
                warn: !!warnings?.has(b._id),
              },
            };
          }),
      ),
    [buses, showHidden, fadeOthers, highlight, warnings],
  );
  const selected = useMemo(
    () => fc(data.features.filter((f) => f.properties?.id === highlight)),
    [data, highlight],
  );

  return (
    <>
      <GeoJsonLayer
        data={selected}
        layers={[
          {
            type: 'circle',
            paint: {
              'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 14, 16, 26] as never,
              'circle-color': '#f5b700',
              'circle-opacity': 0.35,
              'circle-stroke-color': '#f5b700',
              'circle-stroke-width': 2,
            },
          },
        ]}
      />
      <GeoJsonLayer
        data={data}
        onClick={onSelect ? (p) => onSelect(String(p.id)) : undefined}
        layers={[
          {
            type: 'symbol',
            layout: {
              'icon-image': ['get', 'bus'],
              'icon-rotate': ['get', 'heading'],
              'icon-rotation-alignment': 'map',
              'icon-size': iconSize as never,
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'symbol-sort-key': ['get', 'top'],
            },
            paint: { 'icon-opacity': ['*', ['get', 'fade'], ['case', ['get', 'active'], 1, 0.7]] },
          },
          {
            type: 'symbol',
            layout: {
              'icon-image': ['get', 'badge'],
              'icon-size': iconSize as never,
              'icon-anchor': 'bottom-left',
              'icon-offset': [6, -10],
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'symbol-sort-key': ['get', 'top'],
            },
            paint: { 'icon-opacity': ['get', 'fade'] },
          },
          {
            type: 'symbol',
            layout: {
              'icon-image': ['case', ['get', 'warn'], WARN_ICON, ''],
              'icon-size': iconSize as never,
              'icon-anchor': 'bottom-right',
              'icon-offset': [-6, -10],
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
            },
            paint: { 'icon-opacity': ['get', 'fade'] },
          },
        ]}
      />
    </>
  );
}
